import React, { useState } from 'react';
import { X, Bug, Lightbulb, ChatCircle, PaperPlaneTilt, Spinner, CheckCircle } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';

const FeedbackModal = ({ isOpen, onClose }) => {
  const [feedbackType, setFeedbackType] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const feedbackTypes = [
    { id: 'bug', label: 'Bug Report', icon: Bug, color: 'from-red-500 to-orange-500', description: 'Something broke' },
    { id: 'feature_request', label: 'Feature Idea', icon: Lightbulb, color: 'from-yellow-500 to-amber-500', description: 'I have a suggestion' },
    { id: 'general', label: 'Just Chatting', icon: ChatCircle, color: 'from-blue-500 to-cyan-500', description: 'General thoughts' },
  ];

  const handleSubmit = async () => {
    if (!feedbackType) {
      toast.error('Please select a feedback type');
      return;
    }
    if (!message.trim()) {
      toast.error('Please enter your feedback');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/feedback', {
        feedback_type: feedbackType,
        message: message.trim(),
        page_url: window.location.href
      });
      
      setSubmitted(true);
      setTimeout(() => {
        onClose();
        // Reset state after closing
        setTimeout(() => {
          setFeedbackType('');
          setMessage('');
          setSubmitted(false);
        }, 300);
      }, 2000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setFeedbackType('');
      setMessage('');
      setSubmitted(false);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="paygbot-modal-shell relative w-full max-w-md bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">Tell Me What You Think</h2>
              <p className="text-sm text-slate-400">Your feedback helps me get better</p>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={18} className="text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-white" weight="fill" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Thanks for that!</h3>
                <p className="text-slate-400">I really appreciate your feedback.</p>
              </motion.div>
            ) : (
              <div className="space-y-5">
                {/* Feedback Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-3">
                    What type of feedback?
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {feedbackTypes.map((type) => {
                      const Icon = type.icon;
                      const isSelected = feedbackType === type.id;
                      return (
                        <button
                          key={type.id}
                          onClick={() => setFeedbackType(type.id)}
                          data-testid={`feedback-type-${type.id}`}
                          className={`p-3 rounded-xl border transition-all text-center ${
                            isSelected
                              ? `bg-gradient-to-br ${type.color} border-transparent`
                              : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                          }`}
                        >
                          <Icon 
                            size={24} 
                            weight="fill" 
                            className={`mx-auto mb-1 ${isSelected ? 'text-white' : 'text-slate-400'}`} 
                          />
                          <span className={`text-xs font-medium ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                            {type.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Message Input */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Your feedback
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    data-testid="feedback-message"
                    placeholder={
                      feedbackType === 'bug'
                        ? "Describe what happened and what you expected..."
                        : feedbackType === 'feature_request'
                        ? "Describe the feature you'd like to see..."
                        : "Share your thoughts with us..."
                    }
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={4}
                  />
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !feedbackType || !message.trim()}
                  data-testid="feedback-submit"
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all"
                >
                  {submitting ? (
                    <>
                      <Spinner size={20} className="animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <PaperPlaneTilt size={20} weight="fill" />
                      <span>Submit Feedback</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default FeedbackModal;
